import  { useState, useCallback } from 'react';
import { Card, CardContent,  CardHeader } from '@shadcn/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shadcn/components/ui/tabs';
import { Button } from '@shadcn/components/ui/button';
import { Badge } from '@shadcn/components/ui/badge';

import { 
  Plus,
  AlertCircle
} from 'lucide-react';

// Import our custom components and hooks
import { useStatusMappings } from '../hooks/useStatusMappings';

import { TicketTypeIcon } from '../components/TicketSystem/TicketTypeIcon';

import { 
  TICKET_TYPES, 
  TICKET_TYPE_LABELS, 
   
} from '../constants';


import StatusMappingTable from '../components/StatusMapping/StatusMappingTable';
import StatusMappingForm from '../components/StatusMapping/StatusMappingForm';



const StatusMappingPage = () => {

  const [activeTab, setActiveTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  


  const {
    mappings,
    
    error,
    isLoading,
    createMapping,
    updateMapping,
    deleteMapping,
    filterByTicketType,
    clearFilters,
  } = useStatusMappings();


  const handleTabChange = useCallback((value) => {
    setActiveTab(value);
    
    if (value === 'all') {
      clearFilters();
    } else {
      filterByTicketType(value);
    }
  }, [clearFilters, filterByTicketType]);

  const handleCreateMapping = useCallback(() => {
    setEditingMapping(null);
    setShowForm(true);
  }, []);

  

  
  const handleEditMapping = useCallback((mapping) => {
    setEditingMapping(mapping);
    setShowForm(true);
  }, []);

  
  const handleFormSubmit = useCallback(async (formData) => {
    let result;
    
    if (editingMapping) {
      result = await updateMapping(editingMapping.id, formData);
    } else {
      result = await createMapping(formData);
    }
    
    if (result.success) {
      setShowForm(false);
      setEditingMapping(null);
      console.log(result.message);
    } else {
      
      console.error(result.error);
    }
    
    return result;
  }, [editingMapping, updateMapping, createMapping]);

  
  const handleDeleteMapping = useCallback(async (id) => {
    
      const result = await deleteMapping(id);
      
      if (result.success) {
        console.log(result.message);
      } else {
        console.error(result.error);
      }
    
  }, [deleteMapping]);

  

  

  
  const getTabCounts = useCallback(() => {
    const counts = {
      all: mappings.length,
      [TICKET_TYPES.ORDER]: 0,
      [TICKET_TYPES.REPAIR]: 0,
      [TICKET_TYPES.WARRANTY]: 0
    };
    
    mappings.forEach(mapping => {
      if (counts.hasOwnProperty(mapping.ticketType)) {
        counts[mapping.ticketType]++;
      }
    });
    
    return counts;
  }, [mappings]);

  const tabCounts = getTabCounts();

  return (
    <div class="page-content">
    <div className="container mx-auto p-6 space-y-6">
      
      <div className="flex items-center justify-between">
        
        
       
      </div>

      
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      

      
      <Card>
        <CardHeader>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Status Mapping Management</h1>
          
        </div>
          <div className="flex items-center justify-between">
            <div />
            <Button onClick={handleCreateMapping}>
              <Plus className="w-4 h-4 mr-2" />
              Add Mapping
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all" className="flex items-center gap-2">
                All
                <Badge variant="secondary" className="ml-1">
                  {tabCounts.all}
                </Badge>
              </TabsTrigger>
              
              {Object.values(TICKET_TYPES).map(ticketType => (
                <TabsTrigger 
                  key={ticketType} 
                  value={ticketType}
                  className="flex items-center gap-2"
                >
                  <TicketTypeIcon type={ticketType} size="xs" showBackground={false} />
                  {TICKET_TYPE_LABELS[ticketType]}
                  <Badge variant="secondary" className="ml-1">
                    {tabCounts[ticketType]}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            
            <div className="mt-6">
              <TabsContent value="all" className="mt-0">
                <StatusMappingTable
                  mappings={mappings}
                  loading={isLoading}
                  onEdit={handleEditMapping}
                  onDelete={handleDeleteMapping}
                />
              </TabsContent>
              
              {Object.values(TICKET_TYPES).map(ticketType => (
                <TabsContent key={ticketType} value={ticketType} className="mt-0">
                  <div className="space-y-4">
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <TicketTypeIcon type={ticketType} size="md" />
                        <div>
                          <h3 className="font-medium">{TICKET_TYPE_LABELS[ticketType]} Mappings</h3>
                          <p className="text-sm text-muted-foreground">
                            {tabCounts[ticketType]} mappings configured
                          </p>
                        </div>
                      </div>
                      
                      
                    </div>
                    
                    <StatusMappingTable
                      mappings={mappings.filter(m => m.ticketType === ticketType)}
                      loading={isLoading}
                      onEdit={handleEditMapping}
                      onDelete={handleDeleteMapping}
                    />
                  </div>
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </CardContent>
      </Card>

      
      {showForm && (
        <StatusMappingForm
          mapping={editingMapping}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingMapping(null);
          }}
        />
      )}

     
    </div>
    </div>
  );
};

export default StatusMappingPage;
